/**
 * Database Service - Supabase data operations
 * Replaces GraphQL/AppSync with Supabase client
 */

console.log('🔧 Loading database service...');

import { supabase } from './supabase';

console.log('✅ Database service loaded, supabase client:', !!supabase);

// =====================
// USER OPERATIONS
// =====================

export const getUserByClerkId = async (clerkId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('clerk_id', clerkId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
};

export const getUserById = async (id) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Find a user by email (case-insensitive). Used to link existing DB user to Clerk
 * when they sign in but no row matches their clerk_id (e.g. account created before
 * or clerk_id was never set). Returns null if no user or multiple users (ambiguous).
 */
export const getUserByEmail = async (email) => {
  if (!email || !String(email).trim()) return null;
  const normalized = String(email).trim().toLowerCase();
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .ilike('email', normalized)
    .limit(2);
  if (error) throw error;
  // Only link if exactly one user has this email
  if (!data || data.length !== 1) return null;
  return data[0];
};

export const createUser = async (userData) => {
  const { data, error } = await supabase
    .from('users')
    .insert([userData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateUser = async (id, updates) => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const listUsers = async (filters = {}) => {
  let query = supabase.from('users').select('*');
  
  // Apply filters
  if (filters.accountType) {
    query = query.eq('account_type', filters.accountType);
  }
  if (filters.isOnline !== undefined) {
    query = query.eq('is_online', filters.isOnline);
  }
  if (filters.limit) {
    query = query.limit(filters.limit);
  }
  if (filters.search) {
    const s = String(filters.search).trim().toLowerCase();
    if (s) query = query.or(`username.ilike.%${s}%,email.ilike.%${s}%,club_name.ilike.%${s}%`);
  }
  
  query = query.order('created_at', { ascending: false });
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Fetch users who have a location set (latitude & longitude not null).
 * Intended for the "Discover Players" list where client-side distance
 * filtering and sorting is applied afterwards.
 */
export const listUsersWithLocation = async (options = {}) => {
  const { limit = 500, accountType } = options;
  let query = supabase
    .from('users')
    .select('*')
    .not('latitude', 'is', null)
    .not('longitude', 'is', null);

  if (accountType) {
    query = query.eq('account_type', accountType);
  }

  query = query.order('last_online_at', { ascending: false, nullsFirst: false });
  query = query.limit(limit);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Fetch users near a given lat/lng within radiusKm.
 * Uses client-side filtering (fine for <1000 users).
 */
export const getNearbyUsers = async (lat, lng, radiusKm = 0.5) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .not('latitude', 'is', null)
    .not('longitude', 'is', null)
    .limit(200);
  if (error) throw error;

  // Haversine client-side filter
  const R = 6371;
  return (data || []).filter(u => {
    const dLat = (u.latitude - lat) * Math.PI / 180;
    const dLon = (u.longitude - lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat * Math.PI / 180) * Math.cos(u.latitude * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c < radiusKm;
  });
};

/**
 * Admin-only: delete a user (e.g. test accounts). Requires clerk_id of an admin.
 */
export const deleteUserAdmin = async (userId, clerkId) => {
  if (!clerkId) throw new Error('clerkId is required');
  const { error } = await supabase.rpc('delete_user_admin', {
    p_user_id: userId,
    p_clerk_id: clerkId,
  });
  if (error) throw error;
};

/**
 * Admin-only: delete a user from both Clerk and Supabase (full cleanup).
 * Use this so the user can re-register with the same email.
 * Falls back to Supabase-only delete if target has no clerk_id or on non-web.
 */
export const deleteUserAdminWithClerk = async (userId, targetClerkId, adminClerkId) => {
  if (!adminClerkId) throw new Error('adminClerkId is required');

  const isWeb = typeof window !== 'undefined';
  const hasTargetClerkId = targetClerkId && String(targetClerkId).trim().length > 0;
  const hostname = typeof window !== 'undefined' ? (window.location?.hostname || '') : '';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');

  // From localhost, calling production API causes CORS (preflight redirect). Use Supabase-only so delete works in dev.
  if (isWeb && isLocalhost) {
    console.warn('delete-user: running on localhost — skipping Clerk delete (Supabase-only). Delete from Clerk dashboard if needed.');
    await deleteUserAdmin(userId, adminClerkId);
    return;
  }

  if (isWeb && hasTargetClerkId) {
    const base = window.location?.origin || '';
    const url = `${base.replace(/\/$/, '')}/api/delete-user`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        targetClerkId: String(targetClerkId).trim(),
        adminClerkId,
      }),
    });
    const text = await res.text();
    const data = text ? (() => { try { return JSON.parse(text); } catch (_) { return {}; } })() : {};
    if (!res.ok) {
      if (res.status === 404) {
        console.warn('delete-user API not found (404), falling back to Supabase-only delete');
        await deleteUserAdmin(userId, adminClerkId);
        return;
      }
      throw new Error(data.message || `Failed to delete user (${res.status})`);
    }
    return;
  }

  await deleteUserAdmin(userId, adminClerkId);
};

/**
 * List club accounts (users with account_type = 'CLUB').
 * @param {Object} filters - Optional: { claimedBy: userId } to only return clubs owned by that user
 */
export const listClubs = async (filters = {}) => {
  let query = supabase
    .from('users')
    .select('id, club_name, address, city, latitude, longitude, claimed_by, created_at')
    .eq('account_type', 'CLUB');
  if (filters.claimedBy) {
    query = query.eq('claimed_by', filters.claimedBy);
  }
  query = query.order('created_at', { ascending: false });
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Delete a club that the current user owns (claimed_by = claimantUserId).
 * Use for removing test clubs from the map.
 */
export const deleteMyClub = async (clubUserId, claimantUserId) => {
  if (!clubUserId || !claimantUserId) throw new Error('clubUserId and claimantUserId are required');
  const { error } = await supabase.rpc('delete_my_club', {
    p_club_user_id: clubUserId,
    p_claimant_user_id: claimantUserId,
  });
  if (error) throw error;
};

/**
 * Search unclaimed clubs by name or address
 * Returns clubs that have no claimed_by and match the search query
 */
export const searchUnclaimedClubs = async (searchQuery, options = {}) => {
  const { limit = 20, city = null } = options || {};
  let query = supabase
    .from('users')
    .select('id, username, club_name, address, city, about, profile_picture_url, approval_status, tier')
    .eq('account_type', 'CLUB')
    .is('claimed_by', null);
  
  if (city) {
    query = query.eq('city', city);
  }

  if (searchQuery && searchQuery.trim()) {
    const search = searchQuery.trim().toLowerCase();
    // Search in club_name, username, address, or city
    query = query.or(`club_name.ilike.%${search}%,username.ilike.%${search}%,address.ilike.%${search}%,city.ilike.%${search}%`);
  }
  
  query = query.order('club_name', { ascending: true }).limit(limit);
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Get all unclaimed clubs (for browse mode)
 */
export const getUnclaimedClubs = async (options = {}) => {
  const { limit = 50, city = null } = options || {};
  let query = supabase
    .from('users')
    .select('id, username, club_name, address, city, about, profile_picture_url, approval_status, tier')
    .eq('account_type', 'CLUB')
    .is('claimed_by', null);

  if (city) {
    query = query.eq('city', city);
  }

  const { data, error } = await query
    .order('club_name', { ascending: true })
    .limit(limit);
  
  if (error) throw error;
  return data || [];
};

// Claimable categories: all except PARK and PUBLIC_SQUARE (can't have owners)
export const CLAIMABLE_CATEGORIES = ['CLUB', 'BAR', 'CAFE', 'UNIVERSITY', 'LIBRARY', 'OTHER'];

/**
 * Search claimable hotspots by name or address
 * Returns hotspots that are claimable (have an owner) and unclaimed
 * Excludes PARK and PUBLIC_SQUARE - types that can't realistically have an owner
 */
export const searchClaimableHotspots = async (searchQuery, options = {}) => {
  const { limit = 100, city = null, categories = null, tags = null } = options || {};
  const cats = Array.isArray(categories) && categories.length > 0 ? categories : CLAIMABLE_CATEGORIES;
  let query = supabase
    .from('hotspots')
    .select('id, name, address, city, category, tags, claim_status')
    .eq('claim_status', 'UNCLAIMED')
    .eq('status', 'PUBLISHED')
    .in('category', cats);

  if (city && city.trim()) {
    query = query.ilike('city', `%${city.trim()}%`);
  }

  if (Array.isArray(tags) && tags.length > 0) {
    // Filter hotspots that have ANY of the selected tags
    query = query.overlaps('tags', tags.map(t => t.toLowerCase().replace(/\s+/g, '-')));
  }

  if (searchQuery && searchQuery.trim()) {
    const search = searchQuery.trim();
    query = query.or(`name.ilike.%${search}%,address.ilike.%${search}%,city.ilike.%${search}%`);
  }

  query = query.order('name', { ascending: true }).limit(limit);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Get all claimable hotspots (for browse mode)
 */
export const getClaimableHotspots = async (options = {}) => {
  const { limit = 100, city = null, categories = null, tags = null } = options || {};
  const cats = Array.isArray(categories) && categories.length > 0 ? categories : CLAIMABLE_CATEGORIES;
  let query = supabase
    .from('hotspots')
    .select('id, name, address, city, category, tags, claim_status')
    .eq('claim_status', 'UNCLAIMED')
    .eq('status', 'PUBLISHED')
    .in('category', cats);

  if (city && city.trim()) {
    query = query.ilike('city', `%${city.trim()}%`);
  }

  if (Array.isArray(tags) && tags.length > 0) {
    query = query.overlaps('tags', tags.map(t => t.toLowerCase().replace(/\s+/g, '-')));
  }

  const { data, error } = await query
    .order('name', { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data || [];
};

/**
 * Submit a claim request for an existing club
 * This creates a pending claim that admin can review
 */
export const submitClubClaimRequest = async (claimData) => {
  const { data, error } = await supabase
    .from('club_claim_requests')
    .insert([{
      club_id: claimData.clubId,
      requester_user_id: claimData.requesterUserId || null,
      requester_email: claimData.email,
      requester_name: claimData.name,
      proof_description: claimData.proofDescription,
      contact_info: claimData.contactInfo,
      status: 'PENDING',
      created_at: new Date().toISOString(),
    }])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Submit a claim request for an existing hotspot
 */
export const submitHotspotClaimRequest = async (claimData) => {
  const now = new Date().toISOString();
  const insertPayload = {
    hotspot_id: claimData.hotspotId,
    requester_user_id: claimData.requesterUserId || null,
    requester_email: claimData.email,
    requester_name: claimData.name,
    proof_description: claimData.proofDescription,
    contact_info: claimData.contactInfo,
    status: 'PENDING',
    created_at: now,
  };
  // Attach evidence document URLs if provided
  if (claimData.evidenceUrls?.length > 0) {
    insertPayload.evidence_urls = claimData.evidenceUrls;
  }
  const { data, error } = await supabase
    .from('club_claim_requests')
    .insert([insertPayload])
    .select()
    .single();

  if (error) throw error;

  // NOTE: We intentionally do NOT change the hotspot's claim_status here.
  // The hotspot stays UNCLAIMED so other users can also submit claims for it.
  // claim_status only changes to APPROVED when an admin approves one claim.
  return data;
};

/**
 * Get pending claim requests (for admin)
 */
export const getPendingClaimRequests = async () => {
  return getClaimRequestsByStatus('PENDING');
};

/**
 * Get claim requests filtered by status (for admin)
 * @param {string} status - 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL'
 */
export const getClaimRequestsByStatus = async (status = 'PENDING') => {
  let query = supabase
    .from('club_claim_requests')
    .select(`
      id,
      club_id,
      hotspot_id,
      requester_user_id,
      requester_email,
      requester_name,
      proof_description,
      contact_info,
      status,
      rejection_reason,
      reviewed_at,
      reviewed_by,
      created_at,
      updated_at,
      evidence_urls,
      club:club_id (
        id,
        username,
        club_name,
        address,
        email
      ),
      hotspot:hotspot_id (
        id,
        name,
        address,
        city,
        category,
        claimable_type
      )
    `)
    .order('created_at', { ascending: false });

  if (status !== 'ALL') {
    query = query.eq('status', status);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Approve a club claim request
 */
export const approveClaimRequest = async (requestId, clubId, adminUserId) => {
  // First, create or get the user for the claimer
  // Then update the club's claimed_by
  // Finally, update the request status
  
  const { data: request, error: reqError } = await supabase
    .from('club_claim_requests')
    .select('*')
    .eq('id', requestId)
    .single();
  
  if (reqError) throw reqError;
  
  if (request.hotspot_id) {
    if (!request.requester_user_id) {
      throw new Error('Requester user is required to approve hotspot claims');
    }

    const { data: hotspot, error: hotspotError } = await supabase
      .from('hotspots')
      .select('id, name, address, city, description')
      .eq('id', request.hotspot_id)
      .single();

    if (hotspotError) throw hotspotError;

    const { error: userError } = await supabase
      .from('users')
      .update({
        account_type: 'CLUB',
        club_name: hotspot.name,
        address: hotspot.address,
        city: hotspot.city || null,
        about: hotspot.description || null,
        approval_status: 'APPROVED',
        claimed_by: request.requester_user_id,
      })
      .eq('id', request.requester_user_id);

    if (userError) throw userError;

    const { error: hotspotClaimError } = await supabase
      .from('hotspots')
      .update({
        claim_status: 'APPROVED',
        claimed_by: request.requester_user_id,
        owner_id: request.requester_user_id,
        claimed_at: new Date().toISOString(),
      })
      .eq('id', request.hotspot_id);

    if (hotspotClaimError) throw hotspotClaimError;
  } else {
    // Update club claimed_by to the requester user id when available
    const claimedById = request.requester_user_id || clubId;
    const { error: clubError } = await supabase
      .from('users')
      .update({ claimed_by: claimedById })
      .eq('id', clubId);
    
    if (clubError) throw clubError;
  }
  
  // Update this request status to APPROVED
  const { data, error } = await supabase
    .from('club_claim_requests')
    .update({ 
      status: 'APPROVED',
      reviewed_at: new Date().toISOString(),
      reviewed_by: adminUserId || null,
    })
    .eq('id', requestId)
    .select()
    .single();
  
  if (error) throw error;

  // Auto-reject all OTHER pending claims for the same hotspot (if hotspot-based claim)
  if (request.hotspot_id) {
    const { error: autoRejectError } = await supabase
      .from('club_claim_requests')
      .update({
        status: 'REJECTED',
        rejection_reason: 'Another claim for this hotspot was approved.',
        reviewed_at: new Date().toISOString(),
        reviewed_by: adminUserId || null,
      })
      .eq('hotspot_id', request.hotspot_id)
      .eq('status', 'PENDING')
      .neq('id', requestId);

    if (autoRejectError) {
      console.error('Auto-reject other claims error:', autoRejectError);
      // Non-fatal — the approval succeeded, just log the error
    }
  }

  return data;
};

/**
 * Reject a club claim request
 */
export const rejectClaimRequest = async (requestId, reason) => {
  // NOTE: We do NOT change the hotspot's claim_status when rejecting a single claim.
  // The hotspot stays UNCLAIMED (or whatever it currently is) so other pending claims
  // can still be processed. The hotspot only changes status when a claim is APPROVED.

  const { data, error } = await supabase
    .from('club_claim_requests')
    .update({ 
      status: 'REJECTED',
      rejection_reason: reason,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', requestId)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// CONVERSATION OPERATIONS
// =====================

export const getConversationById = async (id) => {
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      *,
      conversation_users (
        user_id,
        users (*)
      ),
      messages (
        *,
        sender:users!messages_sender_id_fkey (*),
        receiver:users!messages_receiver_id_fkey (*)
      )
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const getUserConversations = async (userId) => {
  // Get conversation IDs for user
  const { data: conversationUsers, error: cuError } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', userId);
  
  if (cuError) throw cuError;
  
  if (!conversationUsers || conversationUsers.length === 0) {
    return [];
  }
  
  const conversationIds = conversationUsers.map(cu => cu.conversation_id);
  
  // Get conversations with latest message
  // Only include conversations that have messages (last_message_at is not null)
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      *,
      conversation_users (
        user_id,
        users (*)
      ),
      messages (
        *,
        sender:users!messages_sender_id_fkey (*)
      )
    `)
    .in('id', conversationIds)
    .not('last_message_at', 'is', null) // Only conversations with messages
    .order('last_message_at', { ascending: false });
  
  if (error) throw error;
  
  // Additional filter: ensure conversation has at least one message
  const conversationsWithMessages = (data || []).filter(conv => {
    const hasMessages = conv.messages && Array.isArray(conv.messages) && conv.messages.length > 0;
    return hasMessages;
  });
  
  console.log(`📋 Filtered conversations: ${data?.length || 0} total, ${conversationsWithMessages.length} with messages`);
  
  return conversationsWithMessages;
};

export const createConversation = async (userIds) => {
  // Create conversation
  const { data: conversation, error: convError } = await supabase
    .from('conversations')
    .insert([{ last_message_at: new Date().toISOString() }])
    .select()
    .single();
  
  if (convError) throw convError;
  
  // Add users to conversation
  const conversationUsers = userIds.map(userId => ({
    conversation_id: conversation.id,
    user_id: userId,
  }));
  
  const { error: cuError } = await supabase
    .from('conversation_users')
    .insert(conversationUsers);
  
  if (cuError) throw cuError;
  
  return conversation;
};

export const findExistingConversation = async (user1Id, user2Id) => {
  // Find conversations where both users are members
  const { data: user1Convs, error: e1 } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', user1Id);
  
  if (e1) throw e1;
  
  const { data: user2Convs, error: e2 } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', user2Id);
  
  if (e2) throw e2;
  
  // Find intersection
  const user1ConvIds = new Set(user1Convs.map(c => c.conversation_id));
  const commonConvId = user2Convs.find(c => user1ConvIds.has(c.conversation_id));
  
  if (commonConvId) {
    return getConversationById(commonConvId.conversation_id);
  }
  
  return null;
};

// =====================
// MESSAGE OPERATIONS
// =====================

export const getMessagesForConversation = async (conversationId, limit = 50) => {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      *,
      sender:users!messages_sender_id_fkey (*),
      receiver:users!messages_receiver_id_fkey (*)
    `)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) throw error;
  return data?.reverse() || [];
};

export const createMessage = async (messageData) => {
  const { data, error } = await supabase
    .from('messages')
    .insert([messageData])
    .select(`
      *,
      sender:users!messages_sender_id_fkey (*),
      receiver:users!messages_receiver_id_fkey (*)
    `)
    .single();
  
  if (error) throw error;
  
  // Update conversation's last_message_at
  await supabase
    .from('conversations')
    .update({ last_message_at: new Date().toISOString() })
    .eq('id', messageData.conversation_id);
  
  return data;
};

export const markMessageAsRead = async (messageId) => {
  const { data, error } = await supabase
    .from('messages')
    .update({ read: true })
    .eq('id', messageId)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// POST OPERATIONS
// =====================

export const getPostsByAuthor = async (authorId) => {
  const { data, error } = await supabase
    .from('posts')
    .select(`
      *,
      author:users!posts_author_id_fkey (*)
    `)
    .eq('author_id', authorId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const createPost = async (postData) => {
  const { data, error } = await supabase
    .from('posts')
    .insert([postData])
    .select(`
      *,
      author:users!posts_author_id_fkey (*)
    `)
    .single();
  
  if (error) throw error;
  return data;
};

export const deletePost = async (postId) => {
  const { error } = await supabase
    .from('posts')
    .delete()
    .eq('id', postId);
  
  if (error) throw error;
};

// =====================
// EVENT OPERATIONS
// =====================

export const getEventById = async (id) => {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      club:users!events_club_id_fkey (*),
      rsvps (
        *,
        user:users!rsvps_user_id_fkey (*)
      ),
      likes:event_likes (
        *,
        user:users!event_likes_user_id_fkey (*)
      ),
      comments:event_comments (
        *,
        user:users!event_comments_user_id_fkey (*)
      )
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const listEvents = async (filters = {}) => {
  // Include full rsvp/like/comment rows when requested (e.g. EventDiscoveryScreen needs them)
  const rsvpSelect = filters.includeRsvps ? 'rsvps (*)' : 'rsvps (count)';
  const likesSelect = filters.includeRsvps
    ? 'likes:event_likes (*)'
    : 'likes:event_likes (count)';
  const commentsSelect = filters.includeRsvps
    ? `comments:event_comments (
        *,
        user:users!event_comments_user_id_fkey (*)
      )`
    : '';
  let query = supabase
    .from('events')
    .select(`
      *,
      club:users!events_club_id_fkey (*),
      ${rsvpSelect},
      ${likesSelect}
      ${commentsSelect ? `,${commentsSelect}` : ''}
    `);
  
  if (filters.clubId) {
    query = query.eq('club_id', filters.clubId);
  }
  if (filters.eventType) {
    query = query.eq('event_type', filters.eventType);
  }
  if (filters.fromDate) {
    query = query.gte('event_date', filters.fromDate);
  }
  
  query = query.order('event_date', { ascending: true });
  
  if (filters.limit) {
    query = query.limit(filters.limit);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const createEvent = async (eventData) => {
  const { data, error } = await supabase
    .from('events')
    .insert([eventData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateEvent = async (id, updates) => {
  const { data, error } = await supabase
    .from('events')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteEvent = async (id) => {
  const { error } = await supabase
    .from('events')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// =====================
// RSVP OPERATIONS
// =====================

export const createRsvp = async (rsvpData) => {
  const { data, error } = await supabase
    .from('rsvps')
    .insert([rsvpData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteRsvp = async (eventId, userId) => {
  const { error } = await supabase
    .from('rsvps')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', userId);
  
  if (error) throw error;
};

// =====================
// EVENT CHECK-IN OPERATIONS
// =====================

const generateRandomCode = (len = 8) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < len; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Generate a new check-in code for an event and save it. Called when club owner opens check-in screen.
 */
export const generateEventCheckInCode = async (eventId) => {
  const code = generateRandomCode(8);
  const { data, error } = await supabase
    .from('events')
    .update({
      check_in_code: code,
      check_in_code_updated_at: new Date().toISOString(),
    })
    .eq('id', eventId)
    .select('check_in_code, check_in_code_updated_at')
    .single();
  if (error) throw error;
  return data;
};

/**
 * Validate that the provided code matches the event's current check-in code.
 */
export const validateCheckInCode = async (eventId, code) => {
  const { data, error } = await supabase
    .from('events')
    .select('check_in_code')
    .eq('id', eventId)
    .single();
  if (error) throw error;
  return data && data.check_in_code && data.check_in_code === code;
};

/**
 * Record a check-in for a user at an event. Idempotent: duplicate (event_id, user_id) returns existing row.
 */
export const checkInToEvent = async (eventId, userId, checkInMethod) => {
  const { data, error } = await supabase
    .from('event_check_ins')
    .insert({
      event_id: eventId,
      user_id: userId,
      check_in_method: checkInMethod,
      checked_in_at: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) {
    if (error.code === '23505') {
      const { data: existing } = await supabase
        .from('event_check_ins')
        .select()
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .single();
      return existing;
    }
    throw error;
  }
  return data;
};

/**
 * Get a single user's check-in for an event, if any.
 */
export const getEventCheckInForUser = async (eventId, userId) => {
  const { data, error } = await supabase
    .from('event_check_ins')
    .select()
    .eq('event_id', eventId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data;
};

/**
 * Get all check-ins for an event with user details.
 */
export const getEventCheckIns = async (eventId) => {
  const { data, error } = await supabase
    .from('event_check_ins')
    .select(`
      *,
      user:users!event_check_ins_user_id_fkey (id, username, profile_picture_url)
    `)
    .eq('event_id', eventId)
    .order('checked_in_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Update an event's check-in mode (OFF, QR_CODE, LOCATION).
 */
export const updateEventCheckInMode = async (eventId, mode) => {
  const updates = { check_in_mode: mode };
  if (mode !== 'QR_CODE') {
    updates.check_in_code = null;
    updates.check_in_code_updated_at = null;
  }
  const { data, error } = await supabase
    .from('events')
    .update(updates)
    .eq('id', eventId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

/**
 * Haversine distance in meters between two lat/lng points.
 */
const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get RSVPed users whose last location is within radius (meters) of the event location.
 * Optionally syncs event_check_ins with method LOCATION for those users (idempotent).
 */
export const getLocationBasedAttendees = async (eventId, radiusMeters, syncCheckIns = false) => {
  const { data: event, error: eventErr } = await supabase
    .from('events')
    .select('id, location_latitude, location_longitude, check_in_radius')
    .eq('id', eventId)
    .single();
  if (eventErr || !event) throw eventErr || new Error('Event not found');
  const r = radiusMeters ?? event.check_in_radius ?? 200;
  const eventLat = event.location_latitude;
  const eventLng = event.location_longitude;
  if (eventLat == null || eventLng == null) return [];

  const { data: rsvpRows, error: rsvpErr } = await supabase
    .from('rsvps')
    .select('user_id')
    .eq('event_id', eventId);
  if (rsvpErr) throw rsvpErr;
  const userIds = (rsvpRows || []).map((row) => row.user_id);
  if (userIds.length === 0) return [];

  const { data: users, error: usersErr } = await supabase
    .from('users')
    .select('id, latitude, longitude')
    .in('id', userIds)
    .not('latitude', 'is', null)
    .not('longitude', 'is', null);
  if (usersErr) throw usersErr;

  const within = (users || []).filter(
    (u) => haversineMeters(eventLat, eventLng, u.latitude, u.longitude) <= r
  );
  const withinIds = within.map((u) => u.id);

  if (syncCheckIns && withinIds.length > 0) {
    for (const uid of withinIds) {
      await checkInToEvent(eventId, uid, 'LOCATION').catch(() => {});
    }
  }
  return within;
};

/**
 * Attendance analytics for a club: events with RSVP/check-in counts, no-shows, repeat attendees, trends.
 */
export const getEventAttendanceAnalytics = async (clubId) => {
  const { data: events, error: eventsErr } = await supabase
    .from('events')
    .select('id, title, event_date, club_id')
    .eq('club_id', clubId)
    .order('event_date', { ascending: false })
    .limit(100);
  if (eventsErr) throw eventsErr;
  if (!events || events.length === 0) {
    return { events: [], repeatAttendeeIds: [], totalAttendees: 0 };
  }

  const eventIds = events.map((e) => e.id);

  const { data: rsvpCounts, error: rsvpErr } = await supabase
    .from('rsvps')
    .select('event_id')
    .in('event_id', eventIds);
  if (rsvpErr) throw rsvpErr;
  const rsvpsByEvent = {};
  eventIds.forEach((id) => (rsvpsByEvent[id] = 0));
  (rsvpCounts || []).forEach((r) => { rsvpsByEvent[r.event_id] = (rsvpsByEvent[r.event_id] || 0) + 1; });

  const { data: checkIns, error: checkErr } = await supabase
    .from('event_check_ins')
    .select('event_id, user_id')
    .in('event_id', eventIds);
  if (checkErr) throw checkErr;
  const checkInsByEvent = {};
  const userEventCount = {};
  eventIds.forEach((id) => (checkInsByEvent[id] = 0));
  (checkIns || []).forEach((c) => {
    checkInsByEvent[c.event_id] = (checkInsByEvent[c.event_id] || 0) + 1;
    userEventCount[c.user_id] = (userEventCount[c.user_id] || 0) + 1;
  });

  const repeatAttendeeIds = Object.keys(userEventCount).filter((uid) => userEventCount[uid] >= 2);

  const eventsWithStats = events.map((e) => ({
    ...e,
    rsvp_count: rsvpsByEvent[e.id] || 0,
    check_in_count: checkInsByEvent[e.id] || 0,
    no_show_count: Math.max(0, (rsvpsByEvent[e.id] || 0) - (checkInsByEvent[e.id] || 0)),
  }));

  const totalAttendees = (checkIns || []).length;

  return {
    events: eventsWithStats,
    repeatAttendeeIds,
    repeatAttendeeCount: repeatAttendeeIds.length,
    totalAttendees,
  };
};

// =====================
// EVENT LIKE OPERATIONS
// =====================

export const likeEvent = async (eventId, userId) => {
  const { data, error } = await supabase
    .from('event_likes')
    .insert([{ event_id: eventId, user_id: userId }])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const unlikeEvent = async (eventId, userId) => {
  const { error } = await supabase
    .from('event_likes')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', userId);
  
  if (error) throw error;
};

export const createEventComment = async (eventId, userId, content) => {
  const { data, error } = await supabase
    .from('event_comments')
    .insert([{ event_id: eventId, user_id: userId, content }])
    .select(`
      *,
      user:users!event_comments_user_id_fkey (*)
    `)
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// HOTSPOT OPERATIONS
// =====================

/**
 * List hotspots for normal users (only PUBLISHED by default)
 * For admin access, use listHotspotsAdmin instead
 */
export const listHotspots = async (filters = {}) => {
  let query = supabase
    .from('hotspots')
    .select(`
      *,
      reviews:hotspot_reviews (
        *,
        user:users!hotspot_reviews_user_id_fkey (*)
      )
    `);
  
  // IMPORTANT: Only show PUBLISHED hotspots to normal users
  // Admin screens should use listHotspotsAdmin instead
  if (!filters.includeAllStatuses) {
    query = query.eq('status', 'PUBLISHED');
  }
  
  if (filters.category) {
    query = query.eq('category', filters.category);
  }
  if (filters.city) {
    query = query.eq('city', filters.city);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getHotspotById = async (id) => {
  const { data, error } = await supabase
    .from('hotspots')
    .select(`
      *,
      reviews:hotspot_reviews (
        *,
        user:users!hotspot_reviews_user_id_fkey (*)
      ),
      owner:users!hotspots_owner_id_fkey (
        id,
        username,
        profile_picture_url,
        club_name
      )
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Get the hotspot claimed by a club (user id). Returns the first/only hotspot where claimed_by = clubUserId.
 * Used so clubs can manage their venue's photos and so the map can show club popup with venue photos.
 */
export const getHotspotByClaimedBy = async (claimedByUserId) => {
  if (!claimedByUserId) return null;
  const { data, error } = await supabase
    .from('hotspots')
    .select('*')
    .eq('claimed_by', claimedByUserId)
    .eq('status', 'PUBLISHED')
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
};

/**
 * Ensure the club has a venue (hotspot). If they already have one (claimed_by = clubId), returns it.
 * If not, creates one from the club's profile (name, address, etc.) and returns it.
 * Only allowed for approved club accounts. Use when an approved club wants to add photos but has no venue yet.
 */
export const ensureClubVenue = async (clubId) => {
  if (!clubId) throw new Error('clubId required');
  const { data, error } = await supabase.rpc('ensure_club_venue', { p_club_id: clubId });
  if (error) throw error;
  return data;
};

export const createHotspot = async (hotspotData) => {
  // Use RPC function to bypass RLS for admin operations
  // This function checks admin status internally and creates the hotspot
  const { data, error } = await supabase.rpc('create_hotspot_with_admin_check', {
    p_name: hotspotData.name,
    p_description: hotspotData.description,
    p_category: hotspotData.category,
    p_city: hotspotData.city || null,
    p_address: hotspotData.address,
    p_latitude: hotspotData.latitude,
    p_longitude: hotspotData.longitude,
    p_schedule: hotspotData.schedule || null,
    p_activity_level: hotspotData.activity_level || null,
    p_amenities: hotspotData.amenities || null,
    p_photos: hotspotData.photos || null,
    p_confidence: hotspotData.confidence || 'RELATED',
    p_status: hotspotData.status || 'DRAFT',
    p_source: hotspotData.source || 'ADMIN',
    p_created_by: hotspotData.created_by || null,
    // New fields: tags and social links
    p_tags: hotspotData.tags || [],
    p_instagram_url: hotspotData.instagram_url || null,
    p_twitter_url: hotspotData.twitter_url || null,
    p_facebook_url: hotspotData.facebook_url || null,
    p_website_url: hotspotData.website_url || null,
  });
  
  if (error) throw error;
  return data;
};

export const updateHotspot = async (id, updates) => {
  // Use head:true to avoid JSON coercion errors; we don't rely on the row body.
  const { error } = await supabase
    .from('hotspots')
    .update(updates)
    .eq('id', id)
    .select('id', { head: true });

  if (error) throw error;
  return true; // callers care only that the update succeeded
};

export const deleteHotspot = async (id) => {
  const { error } = await supabase
    .from('hotspots')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// =====================
// HOTSPOT REVIEW OPERATIONS
// =====================

// =====================
// ADMIN HOTSPOT OPERATIONS
// =====================

/**
 * List hotspots with admin filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status (DRAFT, PUBLISHED, ARCHIVED)
 * @param {string} filters.category - Filter by category
 * @param {string} filters.confidence - Filter by confidence level
 * @param {string} filters.source - Filter by source
 * @param {string} filters.searchQuery - Search in name/address
 * @param {string} filters.importBatchId - Filter by import batch
 * @param {Date} filters.fromDate - Filter by created_at >= fromDate
 * @param {Date} filters.toDate - Filter by created_at <= toDate
 * @param {number} filters.limit - Limit results
 * @param {number} filters.offset - Offset for pagination
 * @param {boolean} filters.includeAll - If true, returns all statuses (admin only)
 * @param {string} filters.linksVetted - 'vetted' | 'unvetted' | 'no_links' for social links vetting filter
 * @param {string} filters.hasSocialLink - 'instagram' | 'twitter' | 'facebook' to show only hotspots that have that link
 */
export const listHotspotsAdmin = async (filters = {}) => {
  let query = supabase
    .from('hotspots')
    .select(`
      *,
      reviews:hotspot_reviews (count),
      created_by_user:users!hotspots_created_by_fkey (
        id, username, profile_picture_url
      ),
      import_batch:hotspot_import_batches (
        id, created_at, city_name, source
      )
    `);
  
  // Apply filters
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.category) {
    query = query.eq('category', filters.category);
  }
  if (filters.confidence) {
    query = query.eq('confidence', filters.confidence);
  }
  if (filters.source) {
    query = query.eq('source', filters.source);
  }
  if (filters.importBatchId) {
    query = query.eq('import_batch_id', filters.importBatchId);
  }
  if (filters.linksVetted === 'vetted') {
    query = query.eq('links_vetted', true);
  }
  if (filters.linksVetted === 'no_links') {
    query = query.is('instagram_url', null).is('facebook_url', null).is('twitter_url', null);
  }
  if (filters.linksVetted === 'unvetted') {
    query = query.eq('links_vetted', false);
  }
  if (filters.hasSocialLink === 'instagram') {
    query = query.not('instagram_url', 'is', null);
  }
  if (filters.hasSocialLink === 'twitter') {
    query = query.not('twitter_url', 'is', null);
  }
  if (filters.hasSocialLink === 'facebook') {
    query = query.not('facebook_url', 'is', null);
  }
  if (filters.searchQuery) {
    // Use ilike for case-insensitive search
    query = query.or(`name.ilike.%${filters.searchQuery}%,address.ilike.%${filters.searchQuery}%`);
  }
  if (filters.fromDate) {
    query = query.gte('created_at', filters.fromDate);
  }
  if (filters.toDate) {
    query = query.lte('created_at', filters.toDate);
  }
  
  // Order by created_at descending (newest first)
  query = query.order('created_at', { ascending: false });
  
  // Pagination
  if (filters.limit) {
    query = query.limit(filters.limit);
  }
  if (filters.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  let result = data || [];
  // Unvetted: must have at least one social link (already filtered links_vetted = false)
  if (filters.linksVetted === 'unvetted') {
    result = result.filter(
      (h) =>
        (h.instagram_url && h.instagram_url.trim()) ||
        (h.facebook_url && h.facebook_url.trim()) ||
        (h.twitter_url && h.twitter_url.trim())
    );
  }
  // Has link: exclude empty strings
  if (filters.hasSocialLink === 'instagram') {
    result = result.filter((h) => (h.instagram_url || '').trim() !== '');
  }
  if (filters.hasSocialLink === 'twitter') {
    result = result.filter((h) => (h.twitter_url || '').trim() !== '');
  }
  if (filters.hasSocialLink === 'facebook') {
    result = result.filter((h) => (h.facebook_url || '').trim() !== '');
  }
  return result;
};

/**
 * Get hotspots within a bounding box region
 */
export const getHotspotsInRegion = async (bounds, statusFilter = null) => {
  let query = supabase
    .from('hotspots')
    .select('*')
    .gte('latitude', bounds.minLat)
    .lte('latitude', bounds.maxLat)
    .gte('longitude', bounds.minLng)
    .lte('longitude', bounds.maxLng);
  
  if (statusFilter) {
    query = query.eq('status', statusFilter);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Bulk update hotspots status
 * @param {string[]} hotspotIds - Array of hotspot IDs
 * @param {string} action - PUBLISH, ARCHIVE, or DELETE
 * @param {string} confirmText - Required "DELETE" for delete action
 */
export const bulkUpdateHotspots = async (hotspotIds, action, confirmText = null) => {
  if (!hotspotIds || hotspotIds.length === 0) {
    throw new Error('No hotspot IDs provided');
  }
  
  const results = {
    success: [],
    failed: [],
  };
  
  if (action === 'PUBLISH') {
    const { data, error } = await supabase
      .from('hotspots')
      .update({ 
        status: 'PUBLISHED',
        last_verified_at: new Date().toISOString()
      })
      .in('id', hotspotIds)
      .select();
    
    if (error) throw error;
    results.success = data || [];
    
  } else if (action === 'ARCHIVE') {
    const { data, error } = await supabase
      .from('hotspots')
      .update({ status: 'ARCHIVED' })
      .in('id', hotspotIds)
      .select();
    
    if (error) throw error;
    results.success = data || [];
    
  } else if (action === 'DELETE') {
    if (confirmText !== 'DELETE') {
      throw new Error('Must confirm deletion by passing confirmText="DELETE"');
    }
    
    const { error } = await supabase
      .from('hotspots')
      .delete()
      .in('id', hotspotIds);
    
    if (error) throw error;
    results.success = hotspotIds.map(id => ({ id }));
    
  } else {
    throw new Error(`Invalid action: ${action}. Must be PUBLISH, ARCHIVE, or DELETE`);
  }
  
  return results;
};

/**
 * Update single hotspot with admin fields
 */
export const updateHotspotAdmin = async (id, updates) => {
  const { data, error } = await supabase
    .from('hotspots')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// IMPORT BATCH OPERATIONS
// =====================

/**
 * Create a new import batch
 */
export const createImportBatch = async (batchData) => {
  const { data, error } = await supabase
    .from('hotspot_import_batches')
    .insert([batchData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Get import batch by ID
 */
export const getImportBatchById = async (id) => {
  const { data, error } = await supabase
    .from('hotspot_import_batches')
    .select(`
      *,
      created_by_user:users!hotspot_import_batches_created_by_fkey (
        id, username, profile_picture_url
      )
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * List all import batches
 */
export const listImportBatches = async (limit = 50) => {
  const { data, error } = await supabase
    .from('hotspot_import_batches')
    .select(`
      *,
      created_by_user:users!hotspot_import_batches_created_by_fkey (
        id, username, profile_picture_url
      )
    `)
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) throw error;
  return data;
};

/**
 * Update import batch
 */
export const updateImportBatch = async (id, updates) => {
  const { data, error } = await supabase
    .from('hotspot_import_batches')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Get hotspots in a batch
 */
export const getHotspotsInBatch = async (batchId) => {
  console.log(`🔍 Fetching hotspots for batch: ${batchId}`);
  const { data, error } = await supabase
    .from('hotspots')
    .select('*')
    .eq('import_batch_id', batchId)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching hotspots in batch:', error);
    throw error;
  }
  
  console.log(`✅ Found ${data?.length || 0} hotspots in batch ${batchId}`);
  return data || [];
};

/**
 * Perform batch action on all hotspots in a batch
 */
export const batchAction = async (batchId, action, confirmText = null) => {
  // First get all hotspot IDs in this batch
  const hotspots = await getHotspotsInBatch(batchId);
  const hotspotIds = hotspots.map(h => h.id);
  
  if (action === 'EXPORT') {
    // Just return the hotspots data
    return { hotspots, count: hotspots.length };
  }
  
  if (hotspotIds.length === 0) {
    return { success: [], count: 0 };
  }
  
  // Use bulk update for archive/delete
  return bulkUpdateHotspots(hotspotIds, action, confirmText);
};

/**
 * Delete import batch and optionally its hotspots
 */
export const deleteImportBatch = async (batchId, deleteHotspots = false, confirmText = null) => {
  if (deleteHotspots) {
    // Delete hotspots first
    await batchAction(batchId, 'DELETE', confirmText);
  }
  
  // Delete the batch
  const { error } = await supabase
    .from('hotspot_import_batches')
    .delete()
    .eq('id', batchId);
  
  if (error) throw error;
};

// =====================
// DEDUPE HELPERS
// =====================

/**
 * Calculate distance between two coordinates in meters
 */
export const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Calculate string similarity (Dice coefficient)
 */
export const calculateStringSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  if (s1 === s2) return 1;
  if (s1.length < 2 || s2.length < 2) return 0;
  
  const bigrams1 = new Set();
  for (let i = 0; i < s1.length - 1; i++) {
    bigrams1.add(s1.substring(i, i + 2));
  }
  
  let matches = 0;
  for (let i = 0; i < s2.length - 1; i++) {
    if (bigrams1.has(s2.substring(i, i + 2))) {
      matches++;
    }
  }
  
  return (2.0 * matches) / (s1.length - 1 + s2.length - 1);
};

/**
 * Find potential duplicate hotspots
 * @param {Object} candidate - The hotspot to check
 * @param {Object[]} existingHotspots - Array of existing hotspots to check against
 * @param {number} distanceThreshold - Max distance in meters (default 200)
 * @param {number} similarityThreshold - Min name similarity (default 0.3)
 */
export const findDuplicates = (candidate, existingHotspots, distanceThreshold = 200, similarityThreshold = 0.3) => {
  const duplicates = [];
  
  for (const existing of existingHotspots) {
    const distance = calculateDistanceMeters(
      candidate.latitude,
      candidate.longitude,
      existing.latitude,
      existing.longitude
    );
    
    if (distance <= distanceThreshold) {
      const nameSimilarity = calculateStringSimilarity(candidate.name, existing.name);
      const isAddressMatch = candidate.address.toLowerCase() === existing.address.toLowerCase();
      
      if (nameSimilarity >= similarityThreshold || isAddressMatch) {
        duplicates.push({
          matchedHotspot: existing,
          distance,
          nameSimilarity,
          isAddressMatch,
          reason: isAddressMatch 
            ? 'Exact address match' 
            : `Name similarity: ${(nameSimilarity * 100).toFixed(0)}%`,
        });
      }
    }
  }
  
  return duplicates;
};

/**
 * Bulk create hotspots with deduplication
 * Used for AI suggestions and manual imports
 * @param {Object[]} candidates - Array of hotspot candidates
 * @param {Object} options - Options for the import
 * @param {string} options.batchId - Import batch ID
 * @param {string} options.source - Source (AI, ADMIN, USER)
 * @param {string} options.createdBy - User ID of creator
 * @param {string} options.status - Status to assign (default DRAFT)
 * @param {string} options.confidence - Confidence level (default RELATED)
 */
export const bulkCreateHotspotsWithDedupe = async (candidates, options = {}) => {
  const {
    batchId,
    source = 'AI',
    createdBy,
    status = 'DRAFT',
    confidence = 'RELATED',
  } = options;
  
  // Get existing hotspots for dedupe check
  // IMPORTANT: Include ALL statuses (including drafts) for proper deduplication
  const existingHotspots = await listHotspots({ includeAllStatuses: true });
  
  const created = [];
  const duplicates = [];
  
  for (const candidate of candidates) {
    const dupes = findDuplicates(candidate, existingHotspots);
    
    if (dupes.length > 0) {
      console.log(`⚠️ Duplicate found: "${candidate.name}" matches existing hotspot "${dupes[0].matchedHotspot.name}" (${dupes[0].reason})`);
      duplicates.push({
        candidate,
        matches: dupes,
        reason: dupes[0].reason,
        matchedHotspotId: dupes[0].matchedHotspot.id,
      });
    } else {
      // Create the hotspot
      try {
        const hotspotData = {
          name: candidate.name,
          category: candidate.category,
          description: candidate.description,
          address: candidate.address,
          latitude: candidate.latitude,
          longitude: candidate.longitude,
          schedule: candidate.schedule || null,
          activity_level: candidate.activityLevel || null,
          amenities: candidate.amenities || null,
          status,
          confidence,
          source,
          import_batch_id: batchId,
          created_by: createdBy,
        };
        
        const { data, error } = await supabase
          .from('hotspots')
          .insert([hotspotData])
          .select()
          .single();
        
        if (error) {
          console.error(`Failed to create hotspot "${candidate.name}":`, error);
          throw error;
        }
        
        console.log(`✅ Created hotspot "${data.name}" with batch_id: ${data.import_batch_id}`);
        created.push(data);
        
        // Add to existing for subsequent dedupe checks
        existingHotspots.push(data);
      } catch (err) {
        console.error('Error creating hotspot:', err);
        duplicates.push({
          candidate,
          reason: `Error: ${err.message}`,
        });
      }
    }
  }
  
  // Update batch counts if batchId provided
  if (batchId) {
    console.log(`📊 Updating batch ${batchId}: ${created.length} created, ${duplicates.length} duplicates`);
    await updateImportBatch(batchId, {
      total_created: created.length,
      total_duplicates: duplicates.length,
    });
  }
  
  console.log(`✅ Bulk create complete: ${created.length} created, ${duplicates.length} duplicates`);
  
  return {
    created,
    duplicates,
    totalCreated: created.length,
    totalDuplicates: duplicates.length,
  };
};

// =====================
// HOTSPOT REVIEW OPERATIONS
// =====================

export const createHotspotReview = async (reviewData) => {
  const { data, error } = await supabase
    .from('hotspot_reviews')
    .insert([{
      hotspot_id: reviewData.hotspotId,
      user_id: reviewData.userId,
      rating: reviewData.rating,
      comment: reviewData.comment || null,
    }])
    .select(`
      *,
      user:users!hotspot_reviews_user_id_fkey (
        id,
        username,
        profile_picture_url
      )
    `)
    .single();
  
  if (error) throw error;
  
  // Transform to match expected format (camelCase)
  return {
    id: data.id,
    rating: data.rating,
    comment: data.comment,
    createdAt: data.created_at,
    user: data.user ? {
      id: data.user.id,
      username: data.user.username,
      profilePictureUrl: data.user.profile_picture_url,
    } : null,
  };
};

export const getHotspotReviews = async (hotspotId, limit = 20) => {
  const { data, error } = await supabase
    .from('hotspot_reviews')
    .select(`
      *,
      user:users!hotspot_reviews_user_id_fkey (
        id,
        username,
        profile_picture_url
      )
    `)
    .eq('hotspot_id', hotspotId)
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) throw error;
  
  // Transform to match expected format (camelCase)
  return data.map(review => ({
    id: review.id,
    rating: review.rating,
    comment: review.comment,
    createdAt: review.created_at,
    user: review.user ? {
      id: review.user.id,
      username: review.user.username,
      profilePictureUrl: review.user.profile_picture_url,
    } : null,
  }));
};

// =====================
// QUICK PAIR OPERATIONS
// =====================

export const createQuickPairRequest = async (requestData) => {
  const { data, error } = await supabase
    .from('quick_pair_requests')
    .insert([requestData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const getActiveQuickPairRequests = async () => {
  const { data, error } = await supabase
    .from('quick_pair_requests')
    .select(`
      *,
      user:users!quick_pair_requests_user_id_fkey (*)
    `)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const deactivateQuickPairRequest = async (id) => {
  const { data, error } = await supabase
    .from('quick_pair_requests')
    .update({ is_active: false })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Get a specific quick pair request by ID (regardless of active status)
export const getQuickPairRequestById = async (id) => {
  const { data, error } = await supabase
    .from('quick_pair_requests')
    .select(`
      *,
      user:users!quick_pair_requests_user_id_fkey (*)
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// GAME OPERATIONS
// =====================

export const createGame = async (gameData) => {
  const { data, error } = await supabase
    .from('games')
    .insert([gameData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const getGameById = async (id) => {
  const { data, error } = await supabase
    .from('games')
    .select(`
      *,
      player1:users!games_player1_id_fkey (*),
      player2:users!games_player2_id_fkey (*)
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const updateGame = async (id, updates) => {
  const { data, error } = await supabase
    .from('games')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Find games between two players (recent, not ended)
export const findGamesBetweenPlayers = async (player1Id, player2Id, minutesAgo = 3) => {
  const cutoffTime = new Date(Date.now() - minutesAgo * 60 * 1000).toISOString();
  
  // Query for games where:
  // (player1_id = player1Id AND player2_id = player2Id) OR (player1_id = player2Id AND player2_id = player1Id)
  // Use simpler syntax: check both combinations separately
  const { data, error } = await supabase
    .from('games')
    .select('*')
    .or(`player1_id.eq.${player1Id},player1_id.eq.${player2Id}`)
    .is('ended_at', null)
    .gte('created_at', cutoffTime)
    .order('created_at', { ascending: false })
    .limit(10); // Get more results to filter client-side
  
  if (error) {
    console.error('Error finding games between players:', error);
    throw error;
  }
  
  // Filter client-side to ensure both players match
  const matchingGames = data?.filter(g => {
    const hasPlayer1 = g.player1_id === player1Id || g.player2_id === player1Id;
    const hasPlayer2 = g.player1_id === player2Id || g.player2_id === player2Id;
    return hasPlayer1 && hasPlayer2;
  }) || [];
  
  return matchingGames;
};

export const getUserGames = async (userId) => {
  const { data, error } = await supabase
    .from('games')
    .select(`
      *,
      player1:users!games_player1_id_fkey (*),
      player2:users!games_player2_id_fkey (*)
    `)
    .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

// =====================
// TOURNAMENT OPERATIONS
// =====================

export const listTournaments = async (filters = {}) => {
  let query = supabase
    .from('tournaments')
    .select(`
      *,
      club:users!tournaments_club_id_fkey (*),
      participants:tournament_participants (count)
    `);
  
  if (filters.clubId) {
    query = query.eq('club_id', filters.clubId);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  
  query = query.order('created_at', { ascending: false });
  
  if (filters.limit) {
    query = query.limit(filters.limit);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getTournamentById = async (id) => {
  const { data, error } = await supabase
    .from('tournaments')
    .select(`
      *,
      club:users!tournaments_club_id_fkey (*),
      participants:tournament_participants (
        *,
        user:users!tournament_participants_user_id_fkey (*)
      ),
      rounds:tournament_rounds (*),
      pairings:tournament_pairings (
        *,
        white_participant:tournament_participants!tournament_pairings_white_participant_id_fkey (*),
        black_participant:tournament_participants!tournament_pairings_black_participant_id_fkey (*)
      ),
      standings:tournament_standings (
        *,
        participant:tournament_participants!tournament_standings_participant_id_fkey (*)
      )
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const createTournament = async (tournamentData) => {
  const { data, error } = await supabase
    .from('tournaments')
    .insert([tournamentData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateTournament = async (id, updates) => {
  const { data, error } = await supabase
    .from('tournaments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteTournament = async (id) => {
  const { error } = await supabase
    .from('tournaments')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// Tournament participants
export const addTournamentParticipant = async (participantData) => {
  const { data, error } = await supabase
    .from('tournament_participants')
    .insert([participantData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateTournamentParticipant = async (id, updates) => {
  const { data, error } = await supabase
    .from('tournament_participants')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Tournament rounds
export const createTournamentRound = async (roundData) => {
  const { data, error } = await supabase
    .from('tournament_rounds')
    .insert([roundData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateTournamentRound = async (id, updates) => {
  const { data, error } = await supabase
    .from('tournament_rounds')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Tournament pairings
export const createTournamentPairing = async (pairingData) => {
  const { data, error } = await supabase
    .from('tournament_pairings')
    .insert([pairingData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateTournamentPairing = async (id, updates) => {
  const { data, error } = await supabase
    .from('tournament_pairings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Tournament standings
export const updateTournamentStandings = async (tournamentId, standings) => {
  // Delete existing standings for this tournament
  await supabase
    .from('tournament_standings')
    .delete()
    .eq('tournament_id', tournamentId);
  
  // Insert new standings
  const { data, error } = await supabase
    .from('tournament_standings')
    .insert(standings)
    .select();
  
  if (error) throw error;
  return data;
};

// =====================
// FRIEND REQUEST OPERATIONS
// =====================

export const sendFriendRequest = async (senderId, receiverId) => {
  if (senderId === receiverId) {
    throw new Error('Cannot send a friend request to yourself');
  }

  if (!senderId || !receiverId) {
    throw new Error('Missing sender or receiver user ID');
  }

  // Do not use .single() — two rows can exist (A→B and B→A), which breaks PostgREST object responses
  const { data: rows, error: checkError } = await supabase
    .from('friend_requests')
    .select('*')
    .or(`and(sender_id.eq.${senderId},receiver_id.eq.${receiverId}),and(sender_id.eq.${receiverId},receiver_id.eq.${senderId})`);

  if (checkError) throw checkError;

  const list = rows || [];
  if (list.length === 0) {
    const { data, error } = await supabase
      .from('friend_requests')
      .insert([{ sender_id: senderId, receiver_id: receiverId }])
      .select(`
        *,
        sender:users!friend_requests_sender_id_fkey (*),
        receiver:users!friend_requests_receiver_id_fkey (*)
      `)
      .single();

    if (error) {
      // Handle race condition: another request may have been inserted after our pre-check.
      if (error.code === '23505') {
        const { data: retryRows, error: retryError } = await supabase
          .from('friend_requests')
          .select('*')
          .or(`and(sender_id.eq.${senderId},receiver_id.eq.${receiverId}),and(sender_id.eq.${receiverId},receiver_id.eq.${senderId})`);
        if (retryError) throw retryError;
        if (retryRows?.length) return retryRows[0];
      }
      throw error;
    }
    return data;
  }

  const accepted = list.find((r) => r.status === 'ACCEPTED');
  if (accepted) return accepted;

  const pendingFromOther = list.find(
    (r) => r.sender_id === receiverId && r.receiver_id === senderId && r.status === 'PENDING'
  );
  if (pendingFromOther) {
    return respondToFriendRequest(pendingFromOther.id, 'ACCEPTED');
  }

  const pendingFromMe = list.find(
    (r) => r.sender_id === senderId && r.receiver_id === receiverId && r.status === 'PENDING'
  );
  if (pendingFromMe) return pendingFromMe;

  const declined = list.find((r) => r.status === 'DECLINED');
  if (declined) {
    // Reuse declined row instead of inserting a duplicate pair.
    const { data: revived, error: reviveError } = await supabase
      .from('friend_requests')
      .update({
        sender_id: senderId,
        receiver_id: receiverId,
        status: 'PENDING',
      })
      .eq('id', declined.id)
      .select(`
        *,
        sender:users!friend_requests_sender_id_fkey (*),
        receiver:users!friend_requests_receiver_id_fkey (*)
      `)
      .single();

    if (reviveError) throw reviveError;
    return revived;
  }

  return list[0];
};

export const getPendingFriendRequests = async (userId) => {
  // Get incoming pending requests
  const { data, error } = await supabase
    .from('friend_requests')
    .select(`
      *,
      sender:users!friend_requests_sender_id_fkey (*),
      receiver:users!friend_requests_receiver_id_fkey (*)
    `)
    .eq('receiver_id', userId)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const getSentFriendRequests = async (userId) => {
  // Get outgoing pending requests
  const { data, error } = await supabase
    .from('friend_requests')
    .select(`
      *,
      sender:users!friend_requests_sender_id_fkey (*),
      receiver:users!friend_requests_receiver_id_fkey (*)
    `)
    .eq('sender_id', userId)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const respondToFriendRequest = async (requestId, status) => {
  const { data, error } = await supabase
    .from('friend_requests')
    .update({ status })
    .eq('id', requestId)
    .select(`
      *,
      sender:users!friend_requests_sender_id_fkey (*),
      receiver:users!friend_requests_receiver_id_fkey (*)
    `)
    .single();
  
  if (error) throw error;
  return data;
};

export const getFriends = async (userId) => {
  // Get all accepted friend requests where user is sender or receiver
  const { data, error } = await supabase
    .from('friend_requests')
    .select(`
      *,
      sender:users!friend_requests_sender_id_fkey (*),
      receiver:users!friend_requests_receiver_id_fkey (*)
    `)
    .eq('status', 'ACCEPTED')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  
  // Extract the friend user from each request
  return (data || []).map(req => {
    const friendUser = req.sender_id === userId ? req.receiver : req.sender;
    return {
      ...friendUser,
      friendshipId: req.id,
      friendsSince: req.updated_at,
    };
  });
};

export const getFriendshipStatus = async (userId1, userId2) => {
  const { data: rows, error } = await supabase
    .from('friend_requests')
    .select('*')
    .or(`and(sender_id.eq.${userId1},receiver_id.eq.${userId2}),and(sender_id.eq.${userId2},receiver_id.eq.${userId1})`);

  if (error) throw error;
  if (!rows?.length) return { status: 'NONE' };

  const accepted = rows.find((r) => r.status === 'ACCEPTED');
  if (accepted) {
    return {
      status: accepted.status,
      requestId: accepted.id,
      isSender: accepted.sender_id === userId1,
    };
  }

  const pendingToMe = rows.find((r) => r.status === 'PENDING' && r.receiver_id === userId1);
  const pendingFromMe = rows.find((r) => r.status === 'PENDING' && r.sender_id === userId1);
  const data = pendingToMe || pendingFromMe || rows[0];

  return {
    status: data.status,
    requestId: data.id,
    isSender: data.sender_id === userId1,
  };
};

export const removeFriend = async (userId, friendId) => {
  // Delete the friend request (friendship)
  const { error } = await supabase
    .from('friend_requests')
    .delete()
    .eq('status', 'ACCEPTED')
    .or(`and(sender_id.eq.${userId},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${userId})`);
  
  if (error) throw error;
};

export const cancelFriendRequest = async (requestId) => {
  const { error } = await supabase
    .from('friend_requests')
    .delete()
    .eq('id', requestId);
  
  if (error) throw error;
};

export const getFriendsCount = async (userId) => {
  const { count, error } = await supabase
    .from('friend_requests')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'ACCEPTED')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`);
  
  if (error) throw error;
  return count || 0;
};

/**
 * Lightweight helper: returns an array of user IDs that are friends with the
 * given user. Avoids loading full user objects (only IDs are needed for
 * location-visibility filtering on the map).
 */
export const getFriendIds = async (userId) => {
  const { data, error } = await supabase
    .from('friend_requests')
    .select('sender_id, receiver_id')
    .eq('status', 'ACCEPTED')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`);

  if (error) throw error;

  return (data || []).map((req) =>
    req.sender_id === userId ? req.receiver_id : req.sender_id
  );
};

export const getPendingRequestsCount = async (userId) => {
  const { count, error } = await supabase
    .from('friend_requests')
    .select('*', { count: 'exact', head: true })
    .eq('receiver_id', userId)
    .eq('status', 'PENDING');
  
  if (error) throw error;
  return count || 0;
};

// =====================
// CHESS PLATFORM VERIFICATION
// =====================

/**
 * Check if a Chess.com or Lichess username is already claimed by another user
 * @param {string} platform - 'chesscom' or 'lichess'
 * @param {string} username - The chess platform username to check
 * @param {string} excludeUserId - Optional: User ID to exclude from check (for updates)
 * @returns {Promise<object|null>} - The user who claimed it, or null if not claimed
 */
export const checkChessUsernameClaimed = async (platform, username, excludeUserId = null) => {
  if (!username || !username.trim()) return null;
  
  const field = platform === 'chesscom' ? 'chesscom_username' : 'lichess_username';
  
  let query = supabase
    .from('users')
    .select('id, username, profile_picture_url')
    .ilike(field, username.trim()); // Case-insensitive match
  
  // Exclude current user if provided
  if (excludeUserId) {
    query = query.neq('id', excludeUserId);
  }
  
  const { data, error } = await query.single();
  
  // PGRST116 = no rows returned (not claimed)
  if (error && error.code !== 'PGRST116') throw error;
  
  return data || null;
};

/**
 * Unlink a chess platform account from a user
 * @param {string} userId - The user's ID
 * @param {string} platform - 'chesscom' or 'lichess'
 */
export const unlinkChessAccount = async (userId, platform) => {
  const updateData = platform === 'chesscom' 
    ? { 
        chesscom_username: null, 
        chesscom_blitz: null, 
        chesscom_rapid: null, 
        chesscom_classical: null,
        chesscom_last_sync: null 
      }
    : { 
        lichess_username: null, 
        lichess_blitz: null, 
        lichess_rapid: null, 
        lichess_classical: null,
        lichess_last_sync: null 
      };
  
  const { data, error } = await supabase
    .from('users')
    .update(updateData)
    .eq('id', userId)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// CLUB APPROVAL OPERATIONS
// =====================

/**
 * Get pending club applications for admin review
 * @param {Object} filters - Filter options
 * @param {number} [filters.limit=50] - Maximum number of results
 * @param {number} [filters.offset=0] - Offset for pagination
 * @param {string} [filters.search] - Search term for club name/email
 * @returns {Promise<Array>}
 */
export const getPendingClubs = async (filters = {}) => {
  const { limit = 50, offset = 0, search = null } = filters;
  
  const { data, error } = await supabase.rpc('get_pending_clubs', {
    p_limit: limit,
    p_offset: offset,
    p_search: search,
  });
  
  if (error) throw error;
  return data || [];
};

/**
 * Get clubs by approval status
 * @param {string} status - 'PENDING', 'APPROVED', or 'REJECTED'
 * @param {Object} options - Additional options
 * @returns {Promise<Array>}
 */
export const getClubsByStatus = async (status, options = {}) => {
  const { limit = 100, search = null } = options;
  
  let query = supabase
    .from('users')
    .select('*')
    .eq('account_type', 'CLUB')
    .eq('approval_status', status)
    .order('submitted_at', { ascending: true });
  
  if (search) {
    query = query.or(`club_name.ilike.%${search}%,email.ilike.%${search}%,username.ilike.%${search}%`);
  }
  
  if (limit) {
    query = query.limit(limit);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Get count of pending clubs
 * @returns {Promise<number>}
 */
export const getPendingClubsCount = async () => {
  const { count, error } = await supabase
    .from('users')
    .select('*', { count: 'exact', head: true })
    .eq('account_type', 'CLUB')
    .eq('approval_status', 'PENDING');
  
  if (error) throw error;
  return count || 0;
};

/**
 * Approve a club application
 * @param {string} clubId - The club's user ID
 * @param {string} adminId - The admin's user ID
 * @returns {Promise<Object>} Updated club data
 */
export const approveClub = async (clubId, adminId) => {
  const { data, error } = await supabase.rpc('approve_club', {
    p_club_id: clubId,
    p_admin_id: adminId,
  });
  
  if (error) throw error;
  return data;
};

/**
 * Admin-only: grant a club Pro tier (no payment). Requires clerk_id of an admin.
 * @param {string} clubId - The club's user ID
 * @param {string} adminClerkId - The admin's Clerk ID (for auth)
 */
export const grantClubPro = async (clubId, adminClerkId) => {
  const { error } = await supabase.rpc('grant_club_pro', {
    p_admin_clerk_id: adminClerkId,
    p_club_id: clubId,
  });
  if (error) throw error;
};

/**
 * Reject a club application
 * @param {string} clubId - The club's user ID
 * @param {string} adminId - The admin's user ID
 * @param {string} reason - Reason for rejection
 * @returns {Promise<Object>} Updated club data
 */
export const rejectClub = async (clubId, adminId, reason) => {
  const { data, error } = await supabase.rpc('reject_club', {
    p_club_id: clubId,
    p_admin_id: adminId,
    p_reason: reason,
  });
  
  if (error) throw error;
  return data;
};

/**
 * Bulk approve multiple club applications
 * @param {Array<string>} clubIds - Array of club user IDs
 * @param {string} adminId - The admin's user ID
 * @returns {Promise<number>} Number of clubs approved
 */
export const bulkApproveClubs = async (clubIds, adminId) => {
  const { data, error } = await supabase.rpc('bulk_approve_clubs', {
    p_club_ids: clubIds,
    p_admin_id: adminId,
  });
  
  if (error) throw error;
  return data;
};

/**
 * Bulk reject multiple club applications
 * @param {Array<string>} clubIds - Array of club user IDs
 * @param {string} adminId - The admin's user ID
 * @param {string} reason - Reason for rejection
 * @returns {Promise<number>} Number of clubs rejected
 */
export const bulkRejectClubs = async (clubIds, adminId, reason) => {
  const { data, error } = await supabase.rpc('bulk_reject_clubs', {
    p_club_ids: clubIds,
    p_admin_id: adminId,
    p_reason: reason,
  });
  
  if (error) throw error;
  return data;
};

/**
 * Check if user can submit a new club application (rate limiting)
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>}
 */
export const canSubmitClub = async (userId) => {
  const { data, error } = await supabase.rpc('can_submit_club', {
    user_id: userId,
  });
  
  if (error) {
    console.error('Error checking club submission eligibility:', error);
    return false;
  }
  return data;
};

/**
 * Submit a club application (creates club user with PENDING status)
 * @param {Object} clubData - Club submission data
 * @returns {Promise<Object>} Created club user
 */
export const submitClubApplication = async (clubData) => {
  const { data, error } = await supabase
    .from('users')
    .insert([{
      ...clubData,
      account_type: 'CLUB',
      approval_status: 'PENDING',
      submitted_at: new Date().toISOString(),
      last_submission_attempt: new Date().toISOString(),
    }])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Update a user's last submission attempt timestamp
 * @param {string} userId - The user's ID
 * @returns {Promise<void>}
 */
export const updateLastSubmissionAttempt = async (userId) => {
  const { error } = await supabase
    .from('users')
    .update({ last_submission_attempt: new Date().toISOString() })
    .eq('id', userId);
  
  if (error) throw error;
};

/**
 * Get club application details (for admin review)
 * @param {string} clubId - The club's user ID
 * @returns {Promise<Object>} Club application details
 */
export const getClubApplication = async (clubId) => {
  const { data, error } = await supabase
    .from('users')
    .select(`
      id,
      username,
      email,
      club_name,
      address,
      about,
      submission_pitch,
      submitted_at,
      reviewed_at,
      reviewed_by,
      rejection_reason,
      approval_status,
      tier,
      instagram_url,
      twitter_url,
      facebook_url,
      website,
      latitude,
      longitude,
      profile_picture_url,
      created_at
    `)
    .eq('id', clubId)
    .eq('account_type', 'CLUB')
    .single();
  
  if (error) throw error;
  return data;
};

// =====================
// CLUB MEMBERSHIP OPERATIONS
// =====================

/**
 * Join a club as a member
 * @param {string} clubId - The club's user ID
 * @param {string} userId - The user's ID who wants to join
 * @returns {Promise<Object>} Created membership
 */
export const joinClub = async (clubId, userId) => {
  // Verify the club exists and is a CLUB account
  const { data: club, error: clubError } = await supabase
    .from('users')
    .select('id, account_type, claimed_by')
    .eq('id', clubId)
    .eq('account_type', 'CLUB')
    .single();
  
  if (clubError || !club) {
    throw new Error('Club not found');
  }
  
  // Prevent club owners from joining their own club
  if (club.claimed_by === userId) {
    throw new Error('Club owners cannot join their own club');
  }
  
  const { data, error } = await supabase
    .from('club_members')
    .insert([{
      club_id: clubId,
      user_id: userId,
      status: 'ACTIVE',
      joined_at: new Date().toISOString(),
    }])
    .select()
    .single();
  
  if (error) {
    if (error.code === '23505') { // Unique constraint violation
      throw new Error('Already a member of this club');
    }
    throw error;
  }
  return data;
};

/**
 * Leave a club (remove membership)
 * @param {string} clubId - The club's user ID
 * @param {string} userId - The user's ID who wants to leave
 * @returns {Promise<void>}
 */
export const leaveClub = async (clubId, userId) => {
  const { error } = await supabase
    .from('club_members')
    .delete()
    .eq('club_id', clubId)
    .eq('user_id', userId);
  
  if (error) throw error;
};

/**
 * Get all members of a club
 * @param {string} clubId - The club's user ID
 * @param {boolean} includeInactive - Whether to include inactive/banned members
 * @param {number} limit - Maximum number of members to return
 * @param {number} offset - Offset for pagination
 * @returns {Promise<Object[]>} List of members with user details
 */
export const getClubMembers = async (clubId, includeInactive = false, limit = 50, offset = 0) => {
  let query = supabase
    .from('club_members')
    .select(`
      id,
      club_id,
      user_id,
      joined_at,
      status,
      user:users!club_members_user_id_fkey (
        id,
        username,
        profile_picture_url,
        city,
        rating,
        account_type
      )
    `)
    .eq('club_id', clubId)
    .order('joined_at', { ascending: false })
    .range(offset, offset + limit - 1);
  
  if (!includeInactive) {
    query = query.eq('status', 'ACTIVE');
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data;
};

/**
 * Check if a user is a member of a club
 * @param {string} clubId - The club's user ID
 * @param {string} userId - The user's ID to check
 * @returns {Promise<boolean>} True if user is an active member
 */
export const isClubMember = async (clubId, userId) => {
  if (!clubId || !userId) return false;
  
  const { data, error } = await supabase
    .from('club_members')
    .select('id, status')
    .eq('club_id', clubId)
    .eq('user_id', userId)
    .eq('status', 'ACTIVE')
    .maybeSingle();
  
  if (error) {
    console.error('Error checking club membership:', error);
    return false;
  }
  
  return !!data;
};

/**
 * Get membership details for a user in a specific club
 * @param {string} clubId - The club's user ID
 * @param {string} userId - The user's ID
 * @returns {Promise<Object|null>} Membership details or null
 */
export const getMembershipDetails = async (clubId, userId) => {
  if (!clubId || !userId) return null;
  
  const { data, error } = await supabase
    .from('club_members')
    .select('*')
    .eq('club_id', clubId)
    .eq('user_id', userId)
    .maybeSingle();
  
  if (error) {
    console.error('Error getting membership details:', error);
    return null;
  }
  
  return data;
};

/**
 * Update member list visibility for a club
 * @param {string} clubId - The club's user ID
 * @param {boolean} isPublic - Whether the member list should be public
 * @returns {Promise<Object>} Updated user record
 */
export const updateMemberListVisibility = async (clubId, isPublic) => {
  const { data, error } = await supabase
    .from('users')
    .update({ member_list_public: isPublic })
    .eq('id', clubId)
    .eq('account_type', 'CLUB')
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Get all clubs a user has joined
 * @param {string} userId - The user's ID
 * @returns {Promise<Object[]>} List of club memberships with club details
 */
export const getMembershipsForUser = async (userId) => {
  const { data, error } = await supabase
    .from('club_members')
    .select(`
      id,
      club_id,
      joined_at,
      status,
      club:users!club_members_club_id_fkey (
        id,
        club_name,
        profile_picture_url,
        address,
        city
      )
    `)
    .eq('user_id', userId)
    .eq('status', 'ACTIVE')
    .order('joined_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

/**
 * Get member count for a club
 * @param {string} clubId - The club's user ID
 * @returns {Promise<number>} Number of active members
 */
export const getClubMemberCount = async (clubId) => {
  const { count, error } = await supabase
    .from('club_members')
    .select('*', { count: 'exact', head: true })
    .eq('club_id', clubId)
    .eq('status', 'ACTIVE');
  
  if (error) {
    console.error('Error getting member count:', error);
    return 0;
  }
  
  return count || 0;
};

/**
 * Update a member's status (for club owners)
 * @param {string} membershipId - The membership record ID
 * @param {string} status - New status ('ACTIVE', 'INACTIVE', 'BANNED')
 * @returns {Promise<Object>} Updated membership
 */
export const updateMemberStatus = async (membershipId, status) => {
  const { data, error } = await supabase
    .from('club_members')
    .update({ status })
    .eq('id', membershipId)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Remove a member from a club (for club owners)
 * @param {string} clubId - The club's user ID
 * @param {string} userId - The user's ID to remove
 * @returns {Promise<void>}
 */
export const removeMemberFromClub = async (clubId, userId) => {
  const { error } = await supabase
    .from('club_members')
    .delete()
    .eq('club_id', clubId)
    .eq('user_id', userId);
  
  if (error) throw error;
};

// =====================
// RECURRING EVENTS
// =====================

export const getRecurringEvents = async (clubId) => {
  const { data, error } = await supabase
    .from('club_recurring_events')
    .select('*')
    .eq('club_id', clubId)
    .order('day_of_week', { ascending: true })
    .order('start_time', { ascending: true });
  if (error) throw error;
  return data;
};

export const getRecurringEventById = async (id) => {
  const { data, error } = await supabase
    .from('club_recurring_events')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
};

export const createRecurringEvent = async (eventData) => {
  const { data, error } = await supabase
    .from('club_recurring_events')
    .insert([eventData])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updateRecurringEvent = async (id, updates) => {
  const { data, error } = await supabase
    .from('club_recurring_events')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteRecurringEvent = async (id) => {
  const { error } = await supabase
    .from('club_recurring_events')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

export const toggleRecurringEvent = async (id, isActive) => {
  return updateRecurringEvent(id, { is_active: isActive });
};

export default {
  // Users
  getUserByClerkId,
  getUserById,
  createUser,
  updateUser,
  listUsers,
  listUsersWithLocation,
  deleteUserAdmin,
  deleteUserAdminWithClerk,
  listClubs,
  deleteMyClub,
  getNearbyUsers,
  searchClaimableHotspots,
  getClaimableHotspots,
  // Conversations
  getConversationById,
  getUserConversations,
  createConversation,
  findExistingConversation,
  // Messages
  getMessagesForConversation,
  createMessage,
  markMessageAsRead,
  // Posts
  getPostsByAuthor,
  createPost,
  deletePost,
  // Events
  getEventById,
  listEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  createRsvp,
  deleteRsvp,
  generateEventCheckInCode,
  validateCheckInCode,
  checkInToEvent,
  getEventCheckIns,
  getEventCheckInForUser,
  updateEventCheckInMode,
  getLocationBasedAttendees,
  getEventAttendanceAnalytics,
  likeEvent,
  unlikeEvent,
  // Hotspots
  listHotspots,
  getHotspotById,
  createHotspot,
  updateHotspot,
  deleteHotspot,
  // Admin Hotspots
  listHotspotsAdmin,
  getHotspotsInRegion,
  bulkUpdateHotspots,
  updateHotspotAdmin,
  // Import Batches
  createImportBatch,
  getImportBatchById,
  listImportBatches,
  updateImportBatch,
  getHotspotsInBatch,
  batchAction,
  deleteImportBatch,
  // Dedupe Helpers
  calculateDistanceMeters,
  calculateStringSimilarity,
  findDuplicates,
  bulkCreateHotspotsWithDedupe,
  // Quick Pair
  createQuickPairRequest,
  getActiveQuickPairRequests,
  getQuickPairRequestById,
  deactivateQuickPairRequest,
  // Games
  createGame,
  getGameById,
  updateGame,
  findGamesBetweenPlayers,
  getUserGames,
  // Tournaments
  listTournaments,
  getTournamentById,
  createTournament,
  updateTournament,
  deleteTournament,
  addTournamentParticipant,
  updateTournamentParticipant,
  createTournamentRound,
  updateTournamentRound,
  createTournamentPairing,
  updateTournamentPairing,
  updateTournamentStandings,
  // Friends
  sendFriendRequest,
  getPendingFriendRequests,
  getSentFriendRequests,
  respondToFriendRequest,
  getFriends,
  getFriendshipStatus,
  removeFriend,
  cancelFriendRequest,
  getFriendsCount,
  getFriendIds,
  getPendingRequestsCount,
  // Chess Platform Verification
  checkChessUsernameClaimed,
  unlinkChessAccount,
  // App Settings
  getAppSetting,
  setAppSetting,
  // Club Approval
  getPendingClubs,
  getClubsByStatus,
  getPendingClubsCount,
  approveClub,
  rejectClub,
  grantClubPro,
  bulkApproveClubs,
  bulkRejectClubs,
  canSubmitClub,
  submitClubApplication,
  updateLastSubmissionAttempt,
  getClubApplication,
  submitHotspotClaimRequest,
  getClaimRequestsByStatus,
  // Club Membership
  joinClub,
  leaveClub,
  getClubMembers,
  isClubMember,
  getMembershipDetails,
  updateMemberListVisibility,
  getMembershipsForUser,
  getClubMemberCount,
  updateMemberStatus,
  removeMemberFromClub,
  // Recurring Events
  getRecurringEvents,
  getRecurringEventById,
  createRecurringEvent,
  updateRecurringEvent,
  deleteRecurringEvent,
  toggleRecurringEvent,
};

// =====================
// APP SETTINGS OPERATIONS
// =====================

/**
 * Get a global app setting by key
 * @param {string} key - Setting key
 * @returns {Promise<any>} Setting value
 */
export const getAppSetting = async (key) => {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No setting found, return null
      return null;
    }
    throw error;
  }
  
  // Return the value, handling JSONB
  // Supabase returns JSONB as parsed JavaScript values (number, string, object, etc.)
  const value = data?.value;
  
  // If it's a string that looks like a number, parse it
  if (typeof value === 'string' && !isNaN(parseFloat(value)) && isFinite(value)) {
    return parseFloat(value);
  }
  
  return value;
};

/**
 * Set a global app setting (admin only). Uses Clerk user ID when Supabase auth
 * is not in use (e.g. Clerk-only). Calls RPC that validates admin via users.clerk_id.
 * @param {string} key - Setting key
 * @param {any} value - Setting value (will be stored as JSONB)
 * @param {string} description - Optional description
 * @param {string} clerkUserId - Clerk user ID (required; from useAuth().clerkUserId)
 * @returns {Promise<any>} The new value
 */
export const setAppSetting = async (key, value, description = null, clerkUserId = null) => {
  if (!clerkUserId) {
    throw new Error('clerkUserId is required to update app settings');
  }

  const { data, error } = await supabase.rpc('set_app_setting', {
    p_key: key,
    p_value: value,
    p_description: description ?? null,
    p_clerk_id: clerkUserId,
  });

  if (error) {
    console.error('setAppSetting RPC error:', error?.message, error?.code, error?.details);
    throw error;
  }
  return data;
};

